import { useState, useRef, useEffect } from 'react'
import api from '@/lib/api'
import { Search, X, Database, Plus } from 'lucide-react'
import type { ProjectInfo } from '@/components/ProjectSearchSelect'

export type { ProjectInfo }

interface Props {
  selected: ProjectInfo[]
  onAdd: (project: ProjectInfo) => void
  onRemove: (projectId: number) => void
  label?: string
  maxCount?: number
}

export default function MultiProjectSelect({ selected, onAdd, onRemove, label = 'Linked Projects', maxCount }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ProjectInfo[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const timeout = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    clearTimeout(timeout.current)
    timeout.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await api.get('/projects/search', { params: { q: query } })
        const alreadySelectedIds = new Set(selected.map(p => p.id))
        setResults((res.data as ProjectInfo[]).filter(p => !alreadySelectedIds.has(p.id)))
        setOpen(true)
      } catch {
        setResults([])
      }
      setLoading(false)
    }, 300)
    return () => clearTimeout(timeout.current)
  }, [query, selected])

  function handleSelect(p: ProjectInfo) {
    onAdd(p)
    setQuery('')
    setOpen(false)
    setResults([])
  }

  const atMax = maxCount !== undefined && selected.length >= maxCount

  return (
    <div ref={ref} className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 block">{label}</label>
        {maxCount && <span className="text-xs text-slate-400">{selected.length}/{maxCount}</span>}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(p => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 text-red-800 border border-red-200 rounded-full"
            >
              <Database size={10} className="shrink-0" />
              {p.project_type}-{p.project_code}
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                className="ml-0.5 hover:text-red-600 text-red-400"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {atMax ? (
        <p className="text-xs text-slate-400 italic">Maximum {maxCount} projects selected</p>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => { if (results.length) setOpen(true) }}
            placeholder="Search to add a project..."
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
          {open && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-56 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-3 text-sm text-slate-400 text-center">Searching...</div>
              ) : results.length === 0 && query.length >= 2 ? (
                <div className="px-3 py-3 text-sm text-slate-400 text-center">No projects found</div>
              ) : (
                results.map(p => (
                  <button key={p.id} type="button"
                    onClick={() => handleSelect(p)}
                    className="w-full px-3 py-2.5 text-left text-sm hover:bg-red-50 border-b border-slate-100 last:border-0 flex items-center gap-2">
                    <Plus size={12} className="text-red-800 shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-red-900">{p.project_type}-{p.project_code}</span>
                        <span className="text-slate-500">{p.client_name}</span>
                      </div>
                      {p.service_expiry_date && (
                        <div className="text-xs text-slate-400 mt-0.5">Expiry: {p.service_expiry_date}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
